<template>
  <default-modal
    v-model="modalOpened"
    name="expenses-export-modal"
    :title="$t('expense_export_modal_title')"
    :actionLabel="$t('export')"
    :successHandler="success ? null : handleExport"
  >
    <template slot="content" v-if="!success">
      <p v-html="$t('expense_export_modal_body')"></p>

      <!-- Customers -->
      <q-field
        :label="$t('expense_supplier')"
        :labelWidth="4"
      >
        <multi-customer-select
          ref="exportExportCustomerSelect"
          v-model="formData.supplier_id"
          :partner-type="partnerType"
          :placeholder="$t('expense_supplier')"
        />
      </q-field>

      <!-- Invoice Date Range -->
      <q-field
        :label="$t('invoice_date')"
        :labelWidth="4"
        :error="errors.has('startDate')"
        :error-label="errors.first('startDate')"
      >
        <div class="row no-wrap justify-between">
          <q-datetime
            v-model="formData.startDate"
            type="date"
            name="startDate"
            :format="$t('date_format')"
            clearable
          />

          <span class="on-right on-left">{{ $t('to') }}</span>

          <q-datetime
            v-model="formData.endDate"
            type="date"
            name="endDate"
            :format="$t('date_format')"
            clearable
          />
        </div>
      </q-field>

      <!-- Export format -->
      <q-field :label="$t('export_format')" :labelWidth="4">
        <q-select v-model="formData.format" :options="formatOptions"></q-select>
      </q-field>
    </template>

    <template slot="content" v-else>
      <check-mark></check-mark>
      <h4 class="text-center">{{ $t('export_successful_title') }}</h4>
      <p class="text-center">{{ $t('export_successful_body') }}</p>
    </template>
  </default-modal>
</template>

<script>
  import CheckMark from 'components/Common/Animations/Checkmark.vue'
  import DefaultModal from 'components/Modal/DefaultModal.vue'
  import { ExpensesClient } from 'src/api/Expenses'
  import MultiCustomerSelect from 'src/components/Common/Inputs/MultiCustomerSelect.vue'
  import { businessPartnerTypes } from 'src/utils/businessPartnerTypes'
  import changeCase from 'change-case-object'
  import { createErrorFromResponse } from 'src/utils/error'
  import { date } from 'quasar'

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    data () {
      return {
        modalOpened: false,
        loading: false,
        success: false,
        formData: {
          supplier_id: [],
          startDate: null,
          endDate: null,
          format: 'PDF'
        }
      }
    },

    computed: {
      formatOptions () {
        return ['PDF', 'DATEV'].map(format => {
          return { label: format, value: format }
        })
      },

      hasStartAndStopDate () {
        return !!this.formData.startDate && !!this.formData.endDate
      },

      partnerType () {
        return [businessPartnerTypes.SUPPLIER, businessPartnerTypes.CUSTOMER_AND_SUPPLIER]
      }
    },

    methods: {
      open () {
        this.modalOpened = true
        this.loading = false
        this.success = false
      },

      handleExport () {
        let data = {}
        if (this.formData.startDate && this.formData.endDate) {
          Object.assign(data, this.formData)
        }

        this.$validator.validateAll(data)
          .then(result => {
            if (!result) {
              return
            }

            if (this.hasStartAndStopDate && date.getDateDiff(this.formData.startDate, this.formData.endDate, 'seconds') > 0) {
              this.errors.add({
                field: 'startDate',
                msg: this.$t('start_before_end')
              })

              return
            }

            this.exportExpenses()
          })
      },

      exportExpenses () {
        this.loading = true

        new ExpensesClient()
          .export(changeCase.snake(this.__getFilters()))
          .then(() => {
            this.loading = false
            this.success = true
          })
          .catch(error => {
            this.loading = false

            createErrorFromResponse(error)
          })
      },

      __getFilters () {
        // Remove null values from filters
        let filters = {}
        Object.keys(this.formData).forEach(data => (this.formData[data] !== null && Object.assign(filters, { [data]: this.formData[data] })))

        // Map suppliers to their id
        if (filters.supplier_id.length) {
          filters.supplier_id = filters.supplier_id.map(customer => customer.id)
        } else {
          delete filters.supplier_id
        }

        return filters
      }
    },

    components: {
      CheckMark,
      MultiCustomerSelect,
      DefaultModal
    }
  }
</script>
